import { useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "../supabaseClient";
import { logger } from "../utils/debugLogger";

interface AuthCallbackProps {
  onLoginSuccess: (session: any) => void;
  onLoginFailure: (errMessage: string) => void;
}

export function AuthCallback({
  onLoginSuccess,
  onLoginFailure,
}: AuthCallbackProps) {
  const hasProcessedOAuth = useRef(false);

  useEffect(() => {
    if (hasProcessedOAuth.current) return;
    hasProcessedOAuth.current = true;

    const finishLogin = async () => {
      try {
        logger.debug("AuthCallback loaded:", window.location.href);

        const hashParams = new URLSearchParams(
          window.location.hash.replace(/^#/, "")
        );

        const searchParams = new URLSearchParams(window.location.search);
        const type = hashParams.get("type") || searchParams.get("type");

        if (type === "recovery") {
          logger.debug("Password recovery callback detected in AuthCallback. Redirecting to /reset-password...");
          window.location.href = `${window.location.origin}/reset-password${window.location.hash || window.location.search}`;
          return;
        }

        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");
        const code = searchParams.get("code");

        logger.debug("OAuth callback check:", {
          hasAccessToken: Boolean(accessToken),
          hasRefreshToken: Boolean(refreshToken),
          hasCode: Boolean(code),
          pathname: window.location.pathname,
        });

        /**
         * IMPORTANT:
         * Do not use Business_scout_user here.
         * AuthCallback must only succeed when Supabase creates a real browser session.
         */

        if (accessToken || refreshToken) {
          if (!accessToken || !refreshToken) {
            throw new Error(
              "OAuth returned incomplete tokens. Please try signing in again."
            );
          }

          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) throw error;
        } else if (code) {
          logger.debug("OAuth code found. Exchanging code for Supabase session...");

          const { error } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );

          if (error) throw error;
        }

        const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
        if (sessionErr) throw sessionErr;

        if (sessionData.session) {
          logger.debug("Supabase session found after OAuth callback.");
          if (window.opener) {
            logger.debug("Posting OAUTH_AUTH_SUCCESS to opener...");
            window.opener.postMessage({ type: "OAUTH_AUTH_SUCCESS", session: sessionData.session }, "*");
            window.close();
            return;
          }
          onLoginSuccess(sessionData.session);
          return;
        }

        throw new Error("No valid Supabase OAuth session was found.");
      } catch (error: any) {
        console.error("OAuth callback error:", error);

        localStorage.removeItem("Business_scout_user");

        if (window.opener) {
          logger.debug("Posting OAUTH_AUTH_FAILURE to opener...");
          window.opener.postMessage({ type: "OAUTH_AUTH_FAILURE", error: error.message || "Failed to finish OAuth login." }, "*");
          window.close();
          return;
        }
        onLoginFailure(error.message || "Failed to finish OAuth login.");
      }
    };

    finishLogin();
  }, [onLoginSuccess, onLoginFailure]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100 flex flex-col justify-center items-center p-4">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        <h2 className="text-xl font-semibold text-white">Signing you in...</h2>
        <p className="text-sm text-slate-400">
          Please wait while we complete your authorization.
        </p>
      </div>
    </div>
  );
}